import cx from 'classnames';
import * as React from 'react';

import ListItem from 'components/ListItem';

import './styles/List.scss';

/**
 * ## Renders an unordered list with various style properties.
 *
 *
 * ```example
 * <List size="small">
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 * </List>
 * ```
 *
 * ```example
 * <List
 *   center
 *   size="medium">
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 * </List>
 * ```
 *
 * ```example
 * <List
 *   delimiter="bullet"
 *   horizontal>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 * </List>
 * ```
 *
 * ```example
 * <List
 *   delimiter="comma"
 *   horizontal>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 * </List>
 * ```
 *
 * ```example
 * <List
 *   delimiter="dash"
 *   horizontal>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 *   <ListItem>ListItem</ListItem>
 * </List>
 * ```
 *
 *
 * # TODO
 * * Style when not horizontal
 */

export type ListSize = 'xlarge' | 'large' | 'medium' | 'small';
export type ListDelimiter = 'bullet' | 'comma' | 'dash';
export type ListMarkerPosition = 'inside' | 'outside';
export type ListMarker = 'number' | 'bullet';

type Props = {
  center?: boolean;
  delimiter?: ListDelimiter;
  horizontal?: boolean;
  markerPosition?: ListMarkerPosition;
  marker?: ListMarker;
  indented?: boolean;
  /** ListItem spacing */
  size?: ListSize;
  children:
    | (React.ReactElement<typeof ListItem> | React.ReactElement<typeof ListItem>[] | null)[]
    | React.ReactElement<typeof ListItem>;
  className?: string;
};

export default class List extends React.Component<Props> {
  render() {
    const {
      center,
      children,
      className,
      delimiter,
      horizontal,
      marker,
      size,
      indented,
      markerPosition,
    } = this.props;
    const Component = marker === 'number' ? 'ol' : 'ul';

    const body = React.Children.map(children, child => {
      if (
        child &&
        ((child as React.ReactElement).type !== ListItem &&
          (child as React.ReactElement).type !== 'li')
      ) {
        return <ListItem>{child}</ListItem>;
      }
      return child;
    });

    return (
      <Component
        className={cx(
          'List',
          delimiter && `List--${delimiter}`,
          marker && `List--${marker}`,
          size && `List--${size}`,
          markerPosition && `List--${markerPosition}`,
          className,
          {
            'List--center': center,
            'List--indented': indented,
            'List--horizontal': horizontal,
          }
        )}>
        {body}
      </Component>
    );
  }
}
